import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { UpdateEventTriggerArg } from '../../../modules/EventTriggers'
import { getErrorString } from '../../../utils/errorUtils'

const splitToNumberArray = (value: string): number[] => (
  value.split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .map((item) => Number(item))
    .filter((num) => !Number.isNaN(num))
)

const splitToStringArray = (value: string): string[] => (
  value.split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
)

const formatMamoriosForInput = (
  mamorios?: IEventTriggerMamorio[],
  fallbackIds?: (string | number)[],
): string => {
  if (mamorios && mamorios.length > 0) {
    return mamorios
      .map((mamorio) => {
        if (mamorio.major !== null && mamorio.minor !== null) {
          return `${mamorio.major}-${mamorio.minor}`
        }
        if (mamorio.major !== null) {
          return String(mamorio.major)
        }
        if (mamorio.minor !== null) {
          return String(mamorio.minor)
        }
        return String(mamorio.id)
      })
      .join(', ')
  }

  if (fallbackIds && fallbackIds.length > 0) {
    return fallbackIds.map((value) => String(value)).join(', ')
  }

  return ''
}

const parseMamorioIdentifiers = (value: string): (string | number)[] => (
  value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .map((item) => {
      if (item.includes('-')) return item
      const numeric = Number(item)
      return Number.isNaN(numeric) ? item : numeric
    })
)

type Props = {
  closeModalFunction: (arg: unknown | void) => void
  modalProps: {
    trigger: IEventTrigger
    onSubmit: (payload: UpdateEventTriggerArg) => Promise<void>
    group?: IGroup | null
  }
}

const EditEventTriggerModal = ({
  closeModalFunction,
  modalProps,
}: Props) => {
  const { trigger, onSubmit, group } = modalProps

  const [name, setName] = useState(trigger.name ?? '')
  const [mailSubject, setMailSubject] = useState(trigger.mail_subject ?? '')
  const [mailBody, setMailBody] = useState(trigger.mail_body ?? '')
  const [cooldown, setCooldown] = useState(
    trigger.cooldown_min !== null && trigger.cooldown_min !== undefined
      ? String(trigger.cooldown_min)
      : '',
  )
  const [active, setActive] = useState(trigger.active)
  const [phoneNotification, setPhoneNotification] = useState(trigger.phone_notification)
  const [phoneText, setPhoneText] = useState(trigger.phone_text ?? '')
  const [emails, setEmails] = useState(() => (
    (trigger.emails && trigger.emails.length > 0
      ? trigger.emails
      : trigger.users?.map((user) => user.email) ?? [])
      .join(', ')
  ))
  const [slackWebhook, setSlackWebhook] = useState(trigger.slack_webhook_url ?? '')
  const [deviceId, setDeviceId] = useState(
    trigger.kind === 'device' && trigger.device_id !== null && trigger.device_id !== undefined
      ? String(trigger.device_id)
      : '',
  )
  const [deviceMamorioInput, setDeviceMamorioInput] = useState(() => (
    trigger.kind === 'device'
      ? formatMamoriosForInput(trigger.mamorios, trigger.mamorio_ids)
      : ''
  ))
  const [stayModeEnabled, setStayModeEnabled] = useState(
    trigger.kind === 'device' ? Boolean(trigger.stay_mode_enabled) : false,
  )
  const [stayMaxGapSeconds, setStayMaxGapSeconds] = useState(
    trigger.kind === 'device' && trigger.stay_max_gap_seconds !== null && trigger.stay_max_gap_seconds !== undefined
      ? String(trigger.stay_max_gap_seconds)
      : '',
  )
  const [thresholdMinutes, setThresholdMinutes] = useState(
    trigger.kind === 'mamorios' && trigger.threshold_minutes !== null && trigger.threshold_minutes !== undefined
      ? String(trigger.threshold_minutes)
      : '',
  )
  const [skipAllLost, setSkipAllLost] = useState(
    trigger.kind === 'mamorios' ? Boolean(trigger.skip_all_lost_notification) : false,
  )
  const [mamorioIds, setMamoriosIds] = useState(() => (
    trigger.kind === 'mamorios' || trigger.kind === 'stay'
      ? formatMamoriosForInput(trigger.mamorios, trigger.mamorio_ids)
      : ''
  ))
  const [relatedDeviceIds, setRelatedDeviceIds] = useState(
    trigger.kind === 'mamorios' && trigger.device_ids?.length
      ? trigger.device_ids.join(', ')
      : '',
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const isMountedRef = useRef(true)
  const groupDisplayName = useMemo(() => (
    group?.name ?? `ID: ${trigger.group_id}`
  ), [group, trigger.group_id])

  useEffect(() => {
    if (trigger.kind !== 'device') {
      return undefined
    }
    setDeviceMamorioInput(formatMamoriosForInput(trigger.mamorios, trigger.mamorio_ids))
    return undefined
  }, [trigger.id, trigger.kind, trigger.mamorio_ids, trigger.mamorios])

  useEffect(() => {
    if (trigger.kind === 'device') {
      return undefined
    }
    setMamoriosIds(formatMamoriosForInput(trigger.mamorios, trigger.mamorio_ids))
    return undefined
  }, [trigger.id, trigger.kind, trigger.mamorio_ids, trigger.mamorios])

  useEffect(() => () => {
    isMountedRef.current = false
  }, [])

  const titleLabel = useMemo(() => (
    trigger.kind === 'device' ? '入場通知' : '退場通知'
  ), [trigger.kind])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setErrorMessage(null)

    const parsedCooldown = cooldown.trim().length === 0 ? null : Number(cooldown)
    const isCooldownDisabledByStay = trigger.kind === 'device' && stayModeEnabled
    let normalizedCooldown: number | null
    if (parsedCooldown === null || Number.isNaN(parsedCooldown)) {
      normalizedCooldown = isCooldownDisabledByStay ? trigger.cooldown_min : null
    } else {
      normalizedCooldown = parsedCooldown
    }
    const parsedDeviceId = deviceId.trim().length === 0 ? null : Number(deviceId)
    const parsedThreshold = thresholdMinutes.trim().length === 0 ? null : Number(thresholdMinutes)
    const parsedStayMaxGap = stayMaxGapSeconds.trim().length === 0 ? null : Number(stayMaxGapSeconds)
    const parsedDeviceMamorioIds = parseMamorioIdentifiers(deviceMamorioInput)
    const parsedMamorioIds = parseMamorioIdentifiers(mamorioIds)

    const payload: UpdateEventTriggerArg = trigger.kind === 'device'
      ? {
        kind: 'device',
        id: trigger.id,
        params: {
          group_id: trigger.group_id,
          name: name.trim(),
          mail_subject: mailSubject.trim() || null,
          mail_body: mailBody.trim() || null,
          cooldown_min: normalizedCooldown,
          active,
          phone_notification: phoneNotification,
          phone_text: phoneText.trim() || null,
          emails: splitToStringArray(emails),
          device_id: Number.isNaN(parsedDeviceId) ? null : parsedDeviceId,
          mamorio_ids: parsedDeviceMamorioIds,
          slack_webhook_url: slackWebhook.trim() || null,
          stay_mode_enabled: stayModeEnabled,
          stay_max_gap_seconds: Number.isNaN(parsedStayMaxGap) ? null : parsedStayMaxGap,
        },
      }
      : {
        kind: 'mamorios',
        id: trigger.id,
        params: {
          group_id: trigger.group_id,
          name: name.trim(),
          mail_subject: mailSubject.trim() || null,
          mail_body: mailBody.trim() || null,
          cooldown_min: Number.isNaN(parsedCooldown) ? null : parsedCooldown,
          active,
          phone_notification: phoneNotification,
          phone_text: phoneText.trim() || null,
          emails: splitToStringArray(emails),
          threshold_minutes: Number.isNaN(parsedThreshold) ? null : parsedThreshold,
          skip_all_lost_notification: skipAllLost,
          mamorio_ids: parsedMamorioIds,
          device_ids: splitToNumberArray(relatedDeviceIds),
          slack_webhook_url: slackWebhook.trim() || null,
        },
      }

    try {
      await onSubmit(payload)
      closeModalFunction(undefined)
    } catch (error: unknown) {
      if (isMountedRef.current) {
        setErrorMessage(getErrorString(error))
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false)
      }
    }
  }

  const isFormLocked = !active

  type FieldLayoutProps = {
    id: string
    label: string
    description?: string
    control: React.ReactNode
    helper?: React.ReactNode
    isCheckbox?: boolean
  }

  const renderField = ({
    id,
    label,
    description,
    control,
    helper,
    isCheckbox = false,
  }: FieldLayoutProps) => (
    <div className={`event-trigger-modal__field${isCheckbox ? ' event-trigger-modal__field--checkbox' : ''}`} key={id}>
      <div className="event-trigger-modal__field-label">
        <label htmlFor={id}>{label}</label>
        {description && (
          <p className="event-trigger-modal__field-description">{description}</p>
        )}
      </div>
      <div className="event-trigger-modal__field-control">
        {control}
        {helper}
      </div>
    </div>
  )

  const helperList = (
    <div className="event-trigger-modal__helper">
      <div className="event-trigger-modal__helper-title">
        挿入可能な情報
      </div>
      <ul>
        <li>
          <code>{'{EVENT_NAME}'}</code>
          <span>トリガー名</span>
        </li>
        <li>
          <code>{'{MAMORIO_NAME}'}</code>
          <span>対象のMAMORIO名</span>
        </li>
        <li>
          <code>{'{DEVICE_NAME}'}</code>
          <span>検知したデバイス名</span>
        </li>
        <li>
          <code>{'{EVENT_TIME}'}</code>
          <span>検知日時（ISO形式）</span>
        </li>
        <li>
          <code>{'{EVENT_TIME_DATE}'}</code>
          <span>検知日（YYYY-MM-DD）</span>
        </li>
        <li>
          <code>{'{EVENT_TIME_TIME}'}</code>
          <span>検知時刻（HH:MM:SS）</span>
        </li>
      </ul>
    </div>
  )

  const importantFields: FieldLayoutProps[] = [
    {
      id: 'event-trigger-group-name',
      label: '対象グループ',
      control: (
        <span id="event-trigger-group-name" className="event-trigger-modal__group-chip">
          {groupDisplayName}
        </span>
      ),
    },
    {
      id: 'event-trigger-name',
      label: 'トリガー名称',
      control: (
        <input
          id="event-trigger-name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="名称"
          disabled={isFormLocked}
        />
      ),
    },
    {
      id: 'event-trigger-active',
      label: '有効にする',
      description: '通知の配信を開始します。チェックを外すと停止します。',
      control: (
        <input
          id="event-trigger-active"
          type="checkbox"
          checked={active}
          onChange={(event) => setActive(event.target.checked)}
        />
      ),
      isCheckbox: true,
    },
  ]

  if (trigger.kind === 'device') {
    importantFields.push({
      id: 'event-trigger-device-id',
      label: '対象デバイスID',
      description: '滞在や入退場の判定を行うSpotデバイスのIDです。指定したデバイスからの検知だけが通知対象になります。',
      control: (
        <input
          id="event-trigger-device-id"
          type="number"
          value={deviceId}
          onChange={(event) => setDeviceId(event.target.value)}
          placeholder="例: 345657"
          disabled={isFormLocked}
        />
      ),
    })
  }

  if (trigger.kind === 'mamorios') {
    importantFields.push({
      id: 'event-trigger-threshold',
      label: '閾値 (分)',
      description: 'MAMORIOが未検知になってから通知するまでの待機時間です。指定分数を超えると通知を送ります。',
      control: (
        <input
          id="event-trigger-threshold"
          type="number"
          min={0}
          value={thresholdMinutes}
          onChange={(event) => setThresholdMinutes(event.target.value)}
          disabled={isFormLocked}
        />
      ),
    })
  }

  const messagingFields: FieldLayoutProps[] = [
    {
      id: 'event-trigger-mailsubject',
      label: 'メール件名',
      description: 'メール通知で使用される件名です。',
      control: (
        <input
          id="event-trigger-mailsubject"
          type="text"
          value={mailSubject}
          onChange={(event) => setMailSubject(event.target.value)}
          placeholder="メール件名"
          disabled={isFormLocked}
        />
      ),
    },
    {
      id: 'event-trigger-mailbody',
      label: 'メール本文',
      description: '通知メールの本文です。テンプレート変数を使うと検知情報を差し込めます。',
      control: (
        <textarea
          id="event-trigger-mailbody"
          rows={3}
          value={mailBody}
          onChange={(event) => setMailBody(event.target.value)}
          placeholder="メール本文"
          disabled={isFormLocked}
        />
      ),
      helper: helperList,
    },
    {
      id: 'event-trigger-emails',
      label: '通知先メールアドレス',
      description: '通知メールを送るアドレスをカンマ区切りで入力します。',
      control: (
        <input
          id="event-trigger-emails"
          type="text"
          value={emails}
          onChange={(event) => setEmails(event.target.value)}
          placeholder="example@mamorio.jp, user@example.com"
          disabled={isFormLocked}
        />
      ),
    },
    {
      id: 'event-trigger-slack',
      label: 'Slack Webhook URL',
      description: 'Slack通知を送る場合のIncoming Webhook URLです。',
      control: (
        <input
          id="event-trigger-slack"
          type="url"
          value={slackWebhook}
          onChange={(event) => setSlackWebhook(event.target.value)}
          placeholder="https://hooks.slack.com/services/..."
          disabled={isFormLocked}
        />
      ),
    },
    {
      id: 'event-trigger-phone-notification',
      label: '電話通知を送る',
      description: '音声通話での通知を有効化します。受電メッセージは下の欄で指定できます。',
      control: (
        <input
          id="event-trigger-phone-notification"
          type="checkbox"
          checked={phoneNotification}
          onChange={(event) => setPhoneNotification(event.target.checked)}
          disabled={isFormLocked}
        />
      ),
      isCheckbox: true,
    },
    {
      id: 'event-trigger-phone-text',
      label: '電話通知メッセージ',
      description: '電話発報時に読み上げるメッセージです。未入力の場合は既定文が使われます。',
      control: (
        <input
          id="event-trigger-phone-text"
          type="text"
          value={phoneText}
          onChange={(event) => setPhoneText(event.target.value)}
          placeholder="電話通知の文面"
          disabled={isFormLocked || !phoneNotification}
        />
      ),
    },
  ]

  const isCooldownDisabledByStay = trigger.kind === 'device' && stayModeEnabled

  const advancedFields: FieldLayoutProps[] = [
    {
      id: 'event-trigger-cooldown',
      label: 'クールダウン (分)',
      description: '一度通知を送ったのちに指定分数のあいだ通知を送らないようにします。滞在モードが有効な間は利用できません。',
      control: (
        <input
          id="event-trigger-cooldown"
          type="number"
          min={0}
          value={cooldown}
          onChange={(event) => setCooldown(event.target.value)}
          disabled={isFormLocked || isCooldownDisabledByStay}
        />
      ),
    },
  ]

  if (trigger.kind === 'device') {
    advancedFields.push(
      {
        id: 'event-trigger-device-mamorio-ids',
        label: '対象MAMORIO',
        description: 'このトリガーに紐付けるMAMORIOをカンマ区切りで入力します。数値IDまたはmajor-minor形式(例: 111-222)に対応しています。空欄なら全MAMORIOが対象です。',
        control: (
          <input
            id="event-trigger-device-mamorio-ids"
            type="text"
            value={deviceMamorioInput}
            onChange={(event) => setDeviceMamorioInput(event.target.value)}
            placeholder="111-222,333-444"
            disabled={isFormLocked}
          />
        ),
      },
      {
        id: 'event-trigger-stay-mode',
        label: '滞在モードを有効にする',
        description: '滞在判定を行い、指定の検知間隔内で継続検知した場合は通知を送らないようにします。滞在モードを有効にするとクールダウンは自動で無効になります。',
        control: (
          <input
            id="event-trigger-stay-mode"
            type="checkbox"
            checked={stayModeEnabled}
            onChange={(event) => setStayModeEnabled(event.target.checked)}
            disabled={isFormLocked}
          />
        ),
        isCheckbox: true,
      },
      {
        id: 'event-trigger-stay-gap',
        label: '滞在最大検知間隔 (秒)',
        description: '滞在とみなすために許容する検知の空き時間です。滞在モードがオンのときのみ設定できます。',
        control: (
          <input
            id="event-trigger-stay-gap"
            type="number"
            min={0}
            placeholder="300"
            value={stayMaxGapSeconds}
            onChange={(event) => setStayMaxGapSeconds(event.target.value)}
            disabled={isFormLocked || !stayModeEnabled}
          />
        ),
      },
    )
  }

  if (trigger.kind === 'mamorios') {
    advancedFields.push(
      {
        id: 'event-trigger-skip-all-lost',
        label: '一括紛失通知をスキップ',
        description: 'すべてのMAMORIOが紛失した場合、このトリガーからの通知を抑止します。',
        control: (
          <input
            id="event-trigger-skip-all-lost"
            type="checkbox"
            checked={skipAllLost}
            onChange={(event) => setSkipAllLost(event.target.checked)}
            disabled={isFormLocked}
          />
        ),
        isCheckbox: true,
      },
      {
        id: 'event-trigger-mamorio-ids',
        label: '対象MAMORIO ID (カンマ区切り)',
        description: '特定のMAMORIOだけを監視したい場合にIDまたはmajor-minor形式で入力します。',
        control: (
          <input
            id="event-trigger-mamorio-ids"
            type="text"
            value={mamorioIds}
            onChange={(event) => setMamoriosIds(event.target.value)}
            placeholder="111-222, 333-444"
            disabled={isFormLocked}
          />
        ),
      },
      {
        id: 'event-trigger-related-device-ids',
        label: '対象デバイスID (カンマ区切り)',
        description: '通知対象となるSpotデバイスを指定します。',
        control: (
          <input
            id="event-trigger-related-device-ids"
            type="text"
            value={relatedDeviceIds}
            onChange={(event) => setRelatedDeviceIds(event.target.value)}
            placeholder="42, 43"
            disabled={isFormLocked}
          />
        ),
      },
    )
  }

  return (
    <form className="event-trigger-modal modal-form" onSubmit={handleSubmit}>
      <div className="event-trigger-modal__header">
        <div className="event-trigger-modal__legend">
          {titleLabel}
        </div>
        <p className="event-trigger-modal__description">
          {
            trigger.kind === 'device'
              ? '指定デバイスがグループ内のMAMORIOを検知した際にメール・電話・Slackで通知します。個別のMAMORIOごとにクールダウンを管理します。'
              : 'グループに属するMAMORIOが未検知となった場合にメール・電話・Slackで通知します。閾値や除外時間帯、クールダウンを細かく制御できます。'
          }
        </p>
      </div>
      {isFormLocked && (
        <div className="event-trigger-modal__lock-banner">
          現在この通知は無効です。「有効にする」にチェックを入れると設定項目が編集できるようになります。
        </div>
      )}
      <section className="event-trigger-modal__section">
        <div className="event-trigger-modal__section-body">
          {importantFields.map((field) => renderField(field))}
        </div>
      </section>
      <section className="event-trigger-modal__section">
        <h3 className="event-trigger-modal__section-title">メッセージ送信先と内容</h3>
        <div className="event-trigger-modal__section-body">
          {messagingFields.map((field) => renderField(field))}
        </div>
      </section>
      <section className="event-trigger-modal__section">
        <h3 className="event-trigger-modal__section-title">詳細設定</h3>
        <div className="event-trigger-modal__section-body">
          {trigger.kind === 'device' && (
            <p className="event-trigger-modal__section-hint">
              「滞在モードを有効にする」とクールダウンは同時に利用できません。
            </p>
          )}
          {advancedFields.map((field) => renderField(field))}
        </div>
      </section>
      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}
      <button
        type="submit"
        className="btn btn-primary"
        disabled={isSubmitting}
      >
        保存する
      </button>
      <button
        type="button"
        className="btn btn-outline-primary cancel-button"
        onClick={() => closeModalFunction(undefined)}
      >
        キャンセル
      </button>
    </form>
  )
}

export default EditEventTriggerModal
